"use client";

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/authContext';
import { checkWithdrawalLimits, processWithdrawal } from '../../firebase/payments';
import { getAuthHeaders } from '../../firebase/authHeaders';
import axios from 'axios';

const DialogWithdraw = ({ open, onClose }) => {
    const { user } = useAuth();
    const [amount, setAmount] = useState('');
    const [destination, setDestination] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [withdrawalLimits, setWithdrawalLimits] = useState(null);
    const [withdrawalMethod, setWithdrawalMethod] = useState('stripe'); // 'stripe' or 'paypal'

    useEffect(() => {
        if (open && user) {
            loadWithdrawalLimits();
        }
    }, [open, user]);

    const loadWithdrawalLimits = async () => {
        try {
            setLoading(true);
            const response = await checkWithdrawalLimits(user.uid);
            
            if (response.success) {
                setWithdrawalLimits(response.limits);
            } else {
                setError('Failed to load withdrawal limits');
            }
        } catch (error) {
            setError('Error loading withdrawal limits');
            console.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!amount || !destination) {
            setError('Please fill in all fields');
            return;
        }

        const numAmount = parseFloat(amount);
        if (isNaN(numAmount) || numAmount <= 0) {
            setError('Please enter a valid amount');
            return;
        }

        try {
            setLoading(true);
            setError('');

            if (withdrawalMethod === 'paypal') {
                // PayPal withdrawal
                const response = await axios.post(process.env.REACT_APP_PAYMENT_API + '/paypal-withdraw', {
                    paypalEmail: destination,
                    amount: numAmount,
                    userId: user.uid
                }, { headers: await getAuthHeaders() });
                
                if (response.data.success) {
                    alert('PayPal withdrawal successful!');
                    onClose();
                } else {
                    setError(response.data.message || response.data.error || 'PayPal withdrawal failed');
                }
            } else {
                // Stripe withdrawal (existing method)
                const response = await processWithdrawal(numAmount, destination, user.uid);
                
                if (response.success) {
                    alert('Stripe withdrawal successful!');
                    onClose();
                } else {
                    setError(response.message || response.error || 'Stripe withdrawal failed');
                }
            }
        } catch (error) {
            setError('Error processing withdrawal');
            console.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'new_user_restricted': return 'bg-yellow-100 text-yellow-800';
            case 'waiting_period': return 'bg-orange-100 text-orange-800';
            case 'unrestricted': return 'bg-green-100 text-green-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'new_user_restricted': return '⚠️';
            case 'waiting_period': return '⏳';
            case 'unrestricted': return '✅';
            default: return 'ℹ️';
        }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">Withdraw Funds</h3>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700"
                    >
                        ✕
                    </button>
                </div>

                {/* Withdrawal Limits Status */}
                {withdrawalLimits && (
                    <div className="mb-4 p-3 rounded-lg border">
                        <div className="flex items-center mb-2">
                            <span className="mr-2">{getStatusIcon(withdrawalLimits.status)}</span>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(withdrawalLimits.status)}`}>
                                {withdrawalLimits.status.replace('_', ' ').toUpperCase()}
                            </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{withdrawalLimits.message}</p>
                        
                        <div className="grid grid-cols-2 gap-2 text-xs">
                            <div>
                                <span className="text-gray-500">Max Withdrawal:</span>
                                <span className="ml-1 font-medium">${withdrawalLimits.maxWithdrawalAmount}</span>
                            </div>
                            <div>
                                <span className="text-gray-500">Min Withdrawal:</span>
                                <span className="ml-1 font-medium">${withdrawalLimits.minWithdrawalAmount}</span>
                            </div>
                            <div>
                                <span className="text-gray-500">Current Balance:</span>
                                <span className="ml-1 font-medium">${withdrawalLimits.currentBalance}</span>
                            </div>
                            <div>
                                <span className="text-gray-500">Total Withdrawals:</span>
                                <span className="ml-1 font-medium">{withdrawalLimits.totalWithdrawals}</span>
                            </div>
                        </div>

                        {withdrawalLimits.remainingDays > 0 && (
                            <div className="mt-2 p-2 bg-orange-50 rounded">
                                <p className="text-xs text-orange-700">
                                    ⏰ {withdrawalLimits.remainingDays} days remaining until unlimited withdrawals
                                </p>
                            </div>
                        )}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Amount ($)
                        </label>
                        <input
                            type="number"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            placeholder="Enter amount"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            min={withdrawalLimits?.minWithdrawalAmount || 10}
                            max={withdrawalLimits?.maxWithdrawalAmount || 10000}
                            step="0.01"
                        />
                        {withdrawalLimits && (
                            <p className="text-xs text-gray-500 mt-1">
                                Available: ${withdrawalLimits.currentBalance} | 
                                Max: ${withdrawalLimits.maxWithdrawalAmount}
                            </p>
                        )}
                    </div>

                    {/* Withdrawal Method Selection */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Withdrawal Method
                        </label>
                        <div className="flex gap-4">
                            <label className="flex items-center">
                                <input
                                    type="radio"
                                    name="withdrawalMethod"
                                    value="stripe"
                                    checked={withdrawalMethod === "stripe"}
                                    onChange={(e) => setWithdrawalMethod(e.target.value)}
                                    className="mr-2"
                                />
                                <span className="text-sm">Stripe Transfer</span>
                            </label>
                            <label className="flex items-center">
                                <input
                                    type="radio"
                                    name="withdrawalMethod"
                                    value="paypal"
                                    checked={withdrawalMethod === "paypal"}
                                    onChange={(e) => setWithdrawalMethod(e.target.value)}
                                    className="mr-2"
                                />
                                <span className="text-sm">PayPal Payout</span>
                            </label>
                        </div>
                    </div>

                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {withdrawalMethod === 'paypal' ? 'PayPal Email' : 'Destination Account'}
                        </label>
                        <input
                            type={withdrawalMethod === 'paypal' ? 'email' : 'text'}
                            value={destination}
                            onChange={(e) => setDestination(e.target.value)}
                            placeholder={withdrawalMethod === 'paypal' ? 'Enter your PayPal email' : 'Enter Stripe account ID'}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    {error && (
                        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md text-sm">
                            {error}
                        </div>
                    )}

                    <div className="flex space-x-3">
                        <button
                            type="button"//P
                            onClick={onClose}
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
                        >
                            {loading ? 'Processing...' : 
                             withdrawalMethod === 'paypal' ? 'Withdraw to PayPal' : 'Withdraw via Stripe'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default DialogWithdraw;
